/*
 * dateFormat — Datums-Formatierung fuer das Project Dashboard (Phase 4).
 *
 * Spec §3.2:
 *   < 1 min       → "just now"
 *   1 min–59 min  → "N min ago"
 *   1 h–23 h      → "N hr ago"
 *   gestern       → "yesterday"
 *   2 d–7 d       → "N days ago"
 *   > 7 d         → "Mar 5" (gleiches Jahr) / "Mar 5, 2024" (anderes Jahr)
 *
 * Bewusst NICHT `Intl.RelativeTimeFormat` — wir wollen volle Kontrolle
 * ueber Strings und Schwellen.
 *
 * Trash-Modus zeigt statt "Modified" eine Verfalls-Anzeige:
 *   "expires today" / "expires tomorrow" / "expires in N days"
 */

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const TRASH_RETENTION_DAYS = 30

/**
 * Liefert einen relativen oder absoluten Datumsstring.
 * @param {string|Date|null} iso - ISO-String oder Date-Objekt
 * @param {Date} [now=new Date()] - Bezugszeitpunkt (fuer Tests)
 * @returns {string}
 */
export function formatRelativeDate(iso, now = new Date()) {
    if (!iso) return ''
    const d = iso instanceof Date ? iso : new Date(iso)
    if (isNaN(d.getTime())) return ''

    const diffMs = now.getTime() - d.getTime()
    const diffMin = Math.floor(diffMs / 60_000)

    if (diffMin < 1) return 'just now'
    if (diffMin < 60) return `${diffMin} min ago`

    // Kalender-Tag-Diff hat Vorrang vor reinen Stunden-Diffs: ein Eintrag
    // von gestern 21:00 (heute 12:00 = 15 h) ist UX-intuitiv "yesterday",
    // nicht "15 hr ago".
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const entryDay = new Date(d.getFullYear(), d.getMonth(), d.getDate())
    const diffDays = Math.floor((today.getTime() - entryDay.getTime()) / 86_400_000)

    if (diffDays === 0) {
        // Selber Kalender-Tag → Stunden-Anzeige
        const diffHr = Math.floor(diffMin / 60)
        return `${diffHr} hr ago`
    }
    if (diffDays === 1) return 'yesterday'
    if (diffDays >= 2 && diffDays <= 7) return `${diffDays} days ago`

    // Absolut: "Mar 5" bei gleichem Jahr, "Mar 5, 2024" bei anderem.
    const month = MONTHS_SHORT[d.getMonth()]
    const day = d.getDate()
    if (d.getFullYear() === now.getFullYear()) return `${month} ${day}`
    return `${month} ${day}, ${d.getFullYear()}`
}

/**
 * Liefert eine kompakte Verfalls-Anzeige fuer Trash-Items.
 *
 * Wird unter dem Column-Header "Expires in" gerendert; daher kein
 * "expires in"-Praefix mehr in der Zelle. Bleibt bewusst kurz, damit die
 * Spaltenbreite (COL_DATE = 12) reicht — auch fuer den selektierten
 * Bold-Fall im Prop-Mode und den schmaleren Mono-Mode.
 *
 *   diffDays <= 0  → "today"
 *   diffDays == 1  → "tomorrow"
 *   sonst          → "N days"
 *
 * @param {string|Date|null} deletedAt - Zeitpunkt der Soft-Loeschung
 * @param {Date} [now=new Date()]
 * @returns {string}
 */
export function formatTrashExpiry(deletedAt, now = new Date()) {
    if (!deletedAt) return ''
    const del = deletedAt instanceof Date ? deletedAt : new Date(deletedAt)
    if (isNaN(del.getTime())) return ''

    const expiry = new Date(del.getTime() + TRASH_RETENTION_DAYS * 86_400_000)
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const expiryDay = new Date(expiry.getFullYear(), expiry.getMonth(), expiry.getDate())
    const diffDays = Math.round((expiryDay.getTime() - today.getTime()) / 86_400_000)

    if (diffDays <= 0) return 'today'
    if (diffDays === 1) return 'tomorrow'
    return `${diffDays} days`
}

/**
 * Liefert eine kompakte Groessen-Anzeige (KB / MB).
 * Spec §3.1: 8 Chars Spaltenbreite — "9999 KB" oder "1.4 MB" passen.
 * @param {number} bytes
 * @returns {string}
 */
export function formatSize(bytes) {
    if (typeof bytes !== 'number' || bytes <= 0) return ''
    const kb = bytes / 1024
    if (kb < 1024) {
        // Unter 1 MB: ganzzahlige KB, max 4 Stellen
        return `${Math.max(1, Math.round(kb))} KB`
    }
    const mb = kb / 1024
    // Ueber 1 MB: eine Dezimalstelle
    return `${mb.toFixed(1)} MB`
}
